// What a booking costs at checkout, and when it's paid. BookingForm checks out
// under these rules, and the booking page's panel previews the same booking
// from them, so the two can't tell a client different things.

// Without a full address on file the pro's own place can't be booked (the
// server refuses it too), so a service offered at either place is booked at the
// client's. An at_location-only service never reaches checkout in that state.
export const resolveBookingServiceLocationType = (service, proLocationBookable = true) => {
  const type = String(service?.locationType || "at_location").toLowerCase();
  return type === "both" && !proLocationBookable ? "client_location" : type;
};

// The payment choice checkout opens on: a free reservation when the service
// offers one, its deposit when it takes one, and otherwise the full price.
export const defaultPaymentOption = (service) =>
  service?.allowFreeReservations ? "free" : service?.deposit > 0 ? "deposit" : "full";

// What is charged online at checkout and what is left for the appointment, for
// one set of the client's choices. paymentMode is the service's own (see
// resolveServicePaymentMode); totalServicePrice already counts add-ons and any
// travel fee.
export const deriveBookingPayment = ({
  service,
  paymentMode = "card",
  totalServicePrice = 0,
  paymentOption = defaultPaymentOption(service),
  paymentMethodChoice = "online",
}) => {
  const depositAmount = Number(service?.deposit ?? 0) > 0 ? Number(service.deposit) : 0;
  const isFreeReservation = paymentOption === "free";
  const amountDue =
    paymentOption === "full"
      ? totalServicePrice
      : paymentOption === "deposit"
        ? depositAmount
        : 0;
  const isCompletelyFreeService = totalServicePrice <= 0 && depositAmount <= 0;
  // "cash_only" is the business-wide fallback for a provider who cannot take card
  // payments at all. A business that can take cards may still opt in to offering
  // in-person payment alongside them, in which case the customer picks.
  const isCashOnly = paymentMode === "cash_only";
  const canChoosePaymentMethod =
    !isCashOnly &&
    Boolean(service?.bookingSettings?.inPersonPaymentEnabled) &&
    !isCompletelyFreeService;
  const payInPersonSelected = canChoosePaymentMethod && paymentMethodChoice === "in_person";
  // True whenever nothing is charged at checkout and the provider collects on the
  // day, whether that is the only option or the one the customer chose.
  const paysInPerson = isCashOnly || payInPersonSelected;
  const requiresOnlinePayment = !paysInPerson && !isFreeReservation && amountDue > 0;
  const amountDueToday = requiresOnlinePayment ? amountDue : 0;
  const amountDueAtAppointment =
    totalServicePrice > 0 ? Math.max(totalServicePrice - amountDueToday, 0) : 0;

  return {
    depositAmount,
    isFreeReservation,
    isCompletelyFreeService,
    isCashOnly,
    canChoosePaymentMethod,
    payInPersonSelected,
    paysInPerson,
    requiresOnlinePayment,
    amountDueToday,
    amountDueAtAppointment,
  };
};

const toAmount = (value) => Math.max(Number(value ?? 0) || 0, 0);

// The booking checkout opens on, before the client changes anything: the place
// the form starts on, the payment choice it starts on, and paying online
// wherever that's one of the choices. addOnPrice is for add-ons already ticked.
export const previewBookingPayment = (
  service,
  { paymentMode = "card", proLocationBookable = true, addOnPrice = 0 } = {},
) => {
  const locationType = resolveBookingServiceLocationType(service, proLocationBookable);
  const appliedTravelFee = locationType === "client_location" ? toAmount(service?.travelFee) : 0;
  const total = toAmount(service?.price) + appliedTravelFee + toAmount(addOnPrice);

  return {
    total,
    appliedTravelFee,
    offersFreeReservation: Boolean(service?.allowFreeReservations),
    ...deriveBookingPayment({ service, paymentMode, totalServicePrice: total }),
  };
};
